import { useState, useEffect, useRef } from 'react';
import { Position, PositionAnalysis, DEFAULT_SELL_TIERS } from '@/types/portfolio';
import { fetchMultipleStockPrices } from '@/lib/stockApi';
import { analyzePosition, calculatePortfolioStats } from '@/lib/portfolioAnalysis';
import { fetchExchangeRates, ExchangeRates, convertToCHF } from '@/lib/currencyApi';
import { PortfolioStats } from '@/components/PortfolioStats';
import { PositionCard } from '@/components/PositionCard';
import { StockTable } from '@/components/StockTable';
import { StockScorecard } from '@/components/StockScorecard';
import { AddPositionDialog } from '@/components/AddPositionDialog';
import { ExcelImport } from '@/components/ExcelImport';
import { Auth } from '@/components/Auth';
import { FibonacciRetracement } from '@/components/FibonacciRetracement';
import { PortfolioGainsPieChart } from '@/components/PortfolioGainsPieChart';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { RefreshCw, TrendingUp, LogOut, ArrowUpDown, Clock, FileBarChart, Trash2, ClipboardList, Search } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import tradeCompassLogo from '@/assets/tradecompass-logo.png';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

type SortOption = 'name' | 'totalValue' | 'gainLoss' | 'addedValue';

const Index = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [analyses, setAnalyses] = useState<PositionAnalysis[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates>({ USD: 0.88, EUR: 0.95, CHF: 1 });
  const [timeUntilRefresh, setTimeUntilRefresh] = useState(3600); // 60 minutes in seconds
  const [refreshStatus, setRefreshStatus] = useState<'idle' | 'loading' | 'success' | 'mixed' | 'error'>('idle');
  const [successRate, setSuccessRate] = useState(100);
  const [fetchProgress, setFetchProgress] = useState(0);
  const { toast } = useToast();
  const cardRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const removeDuplicates = async () => {
    if (!user) return;
    
    // Get all positions
    const { data: allPositions } = await supabase
      .from('positions')
      .select('*')
      .order('created_at', { ascending: true });
    
    if (!allPositions) return;
    
    // Group by ticker and keep only the first one
    const seen = new Set<string>();
    const duplicateIds: string[] = [];
    
    allPositions.forEach(pos => {
      if (seen.has(pos.ticker)) {
        duplicateIds.push(pos.id);
      } else {
        seen.add(pos.ticker);
      }
    });
    
    // Delete duplicates if any found
    if (duplicateIds.length > 0) {
      await supabase
        .from('positions')
        .delete()
        .in('id', duplicateIds);
      
      toast({
        title: "Duplicates Removed",
        description: `Removed ${duplicateIds.length} duplicate position${duplicateIds.length !== 1 ? 's' : ''}`,
      });
    }
  };

  const loadPositions = async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from('positions')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      toast({
        title: "Error Loading Positions",
        description: error.message,
        variant: "destructive"
      });
      return;
    }

    setPositions(data.map(p => ({
      id: p.id,
      ticker: (p as any).stock_name || p.ticker,
      buyPrice: Number(p.buy_price),
      quantity: p.quantity,
      totalValue: p.total_value ? Number(p.total_value) : undefined,
      localCurrency: p.local_currency || undefined,
      gainPct: p.gain_pct ? Number(p.gain_pct) : undefined,
      addedValueChf: p.added_value_chf ? Number(p.added_value_chf) : undefined,
      pricePerShare: p.price_per_share ? Number(p.price_per_share) : undefined,
      isin: p.isin || undefined,
      stockCode: p.ticker,
      initialInvestment: (p as any).initial_investment ? Number((p as any).initial_investment) : undefined,
    })));
  };

  useEffect(() => {
    const initializeData = async () => {
      await removeDuplicates();
      await loadPositions();
    };
    
    initializeData();
    
    // Fetch exchange rates on mount and every hour
    const loadRates = async () => {
      const rates = await fetchExchangeRates();
      setExchangeRates(rates);
    };
    
    loadRates();
    const interval = setInterval(loadRates, 60 * 60 * 1000); // Every hour
    
    return () => clearInterval(interval);
  }, [user]);
  
  const fetchPrices = async () => {
    if (positions.length === 0) {
      setAnalyses([]);
      return;
    }
    
    setLoading(true);
    setRefreshStatus('loading');
    setFetchProgress(0);
    try {
      // IG Markets API disabled - using Excel data only
      const prices = new Map(); // Empty price map
      setFetchProgress(100);
      
      let successCount = 0;
      let totalCount = positions.length;
      
      const newAnalyses = positions.map(position => {
        const symbolToUse = position.stockCode || position.ticker;
        const cleanedTicker = symbolToUse
          .replace(/\s*\(.*?\)\s*/g, '')
          .replace(/\s*\|.*$/g, '')
          .replace(/\s*-[A-Z]-\s*/g, '')
          .replace(/\s+/g, '')
          .toUpperCase()
          .trim();
        
        const priceData = prices.get(cleanedTicker);
        const currentPrice = priceData?.price && priceData.price > 0 
          ? priceData.price 
          : (position.pricePerShare || position.buyPrice);
        
        // Count successful fetches
        if (priceData?.price && priceData.price > 0) {
          successCount++;
        }
        
        // Create analysis with Excel data if available
        const analysis = analyzePosition(position, currentPrice, exchangeRates);
        
        // If Excel data provides gainPct, recalculate recovery metrics
        let finalAnalysis = analysis;
        if (position.gainPct !== undefined) {
          const gainPctValue = position.gainPct * 100; // Convert decimal to percentage
          const investedValue = position.buyPrice * position.quantity;
          
          // Find the highest tier that qualifies
          let cumulativeRecoverPct = 0;
          let amountToRecover = 0;
          let matchedTier = null;
          
          for (const tier of DEFAULT_SELL_TIERS.sort((a, b) => b.gainPct - a.gainPct)) {
            if (gainPctValue >= tier.gainPct) {
              matchedTier = tier;
              cumulativeRecoverPct = tier.cumulativeRecoverPct;
              amountToRecover = investedValue * (cumulativeRecoverPct / 100);
              break;
            }
          }
          
          // Calculate shares to sell - use direct percentage if specified
          let recommendedSellQty = 0;
          if (matchedTier?.sellQtyPct !== undefined) {
            recommendedSellQty = Math.floor(position.quantity * (matchedTier.sellQtyPct / 100));
          } else if (amountToRecover > 0) {
            recommendedSellQty = Math.floor(amountToRecover / currentPrice);
          }
          
          const recommendedSellPct = position.quantity > 0
            ? Math.round((recommendedSellQty / position.quantity) * 100)
            : 0;
          
          const currency = position.localCurrency || 'CHF';
          const capitalRecovered = amountToRecover;
          const capitalRecoveredCHF = convertToCHF(capitalRecovered, currency, exchangeRates);
          const remainingCapitalAtRisk = Math.max(0, investedValue - capitalRecovered);
          
          finalAnalysis = {
            ...analysis,
            gainPct: position.gainPct,
            recommendedSellQty,
            recommendedSellPct,
            capitalRecovered: Math.round(capitalRecovered * 100) / 100,
            capitalRecoveredCHF: Math.round(capitalRecoveredCHF * 100) / 100,
            capitalRecoveredPct: cumulativeRecoverPct,
            remainingCapitalAtRisk: Math.round(remainingCapitalAtRisk * 100) / 100
          };
        }
        
        // Override with Excel data if present
        return {
          ...finalAnalysis,
          totalValue: position.totalValue || finalAnalysis.totalValue,
          localCurrency: position.localCurrency,
          gainAmount: position.gainPct !== undefined 
            ? (position.totalValue || finalAnalysis.totalValue) * position.gainPct 
            : finalAnalysis.gainAmount,
          addedValueChf: position.addedValueChf,
          pricePerShare: position.pricePerShare,
          currentPrice,
          isin: position.isin,
          stockCode: position.stockCode
        };
      });
      
      // Calculate success rate
      const rate = (successCount / totalCount) * 100;
      setSuccessRate(rate);
      
      // Set status based on success rate
      if (rate === 100) {
        setRefreshStatus('success');
      } else if (rate === 0) {
        setRefreshStatus('error');
      } else {
        setRefreshStatus('mixed');
      }
      
      setAnalyses(newAnalyses);
      toast({
        title: "Data Loaded",
        description: `Using Excel data for ${totalCount} positions`,
      });
      
      // Reset status after 3 seconds
      setTimeout(() => setRefreshStatus('idle'), 3000);
    } catch (error) {
      setRefreshStatus('error');
      toast({
        title: "Error Fetching Prices",
        description: "Failed to update stock prices. Please try again.",
        variant: "destructive"
      });
      // Reset status after 3 seconds
      setTimeout(() => setRefreshStatus('idle'), 3000);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchPrices();
    setTimeUntilRefresh(3600); // Reset countdown to 60 minutes
    
    // Auto-refresh prices every 60 minutes
    const interval = setInterval(() => {
      fetchPrices();
      setTimeUntilRefresh(3600); // Reset countdown
    }, 3600000); // 60 minutes for IG API updates
    
    return () => clearInterval(interval);
  }, [positions]);
  
  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeUntilRefresh((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);
  
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const handleAddPosition = async (newPosition: Omit<Position, 'id'>) => {
    if (!user) return;
    
    const { error } = await supabase
      .from('positions')
      .insert({
        ticker: newPosition.ticker,
        buy_price: newPosition.buyPrice,
        quantity: newPosition.quantity,
        user_id: user.id
      });

    if (error) {
      toast({
        title: "Error Adding Position",
        description: error.message,
        variant: "destructive"
      });
      return;
    }

    await loadPositions();
    toast({
      title: "Position Added",
      description: `Added ${newPosition.ticker} to portfolio`,
    });
  };
  
  const handleRefreshPrices = () => {
    fetchPrices();
    setTimeUntilRefresh(3600); // Reset countdown on manual refresh
  };
  
  const handleDeletePosition = async (id: string) => {
    const { error } = await supabase
      .from('positions')
      .delete()
      .eq('id', id);

    if (error) {
      toast({
        title: "Error Removing Position",
        description: error.message,
        variant: "destructive"
      });
      return;
    }

    await loadPositions();
    toast({
      title: "Position Removed",
      description: "Position removed from portfolio",
    });
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const handleResetData = async () => {
    if (!user) return;
    
    const { error } = await supabase
      .from('positions')
      .delete()
      .eq('user_id', user.id);

    if (error) {
      toast({
        title: "Error Resetting Data",
        description: error.message,
        variant: "destructive"
      });
      return;
    }

    await loadPositions();
    toast({
      title: "Data Reset",
      description: "All positions have been cleared from the database",
    });
  };

  const scrollToCard = (id: string) => {
    const element = cardRefs.current[id];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Add a brief highlight effect
      element.classList.add('ring-4', 'ring-primary', 'ring-opacity-50');
      setTimeout(() => {
        element.classList.remove('ring-4', 'ring-primary', 'ring-opacity-50');
      }, 2000);
    }
  };

  if (!user) {
    return <Auth />;
  }
  
  const stats = analyses.length > 0 
    ? calculatePortfolioStats(analyses, exchangeRates)
    : { totalInvested: 0, totalValue: 0, totalValueCHF: 0, totalInvestedCHF: 0, totalAddedValueCHF: 0, initialTotalInvestment: 0, totalGain: 0, totalGainPct: 0, positionCount: 0 };
  
  // Calculate alert counts by threshold (15%, 30%, 45%)
  const alertCounts = analyses.reduce((acc, analysis) => {
    const gainPct = analysis.gainPct;
    if (gainPct >= 45) acc.red++;      // 100% recovery
    else if (gainPct >= 30) acc.orange++; // 40% recovery
    else if (gainPct >= 15) acc.yellow++; // 20% recovery
    return acc;
  }, { yellow: 0, orange: 0, red: 0 });
  
  // Sort analyses based on selected option
  const sortedAnalyses = [...analyses].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.ticker.localeCompare(b.ticker);
      case 'totalValue':
        return (b.totalValue || 0) - (a.totalValue || 0);
      case 'gainLoss':
        return (b.gainPct || 0) - (a.gainPct || 0);
      case 'addedValue':
        return (b.addedValueChf || 0) - (a.addedValueChf || 0);
      default:
        return 0;
    }
  });
  
  return (
    <div className="min-h-screen bg-background">
      {/* Progress Bar */}
      {loading && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-foreground min-w-[180px]">
                Refreshing stock prices...
              </span>
              <Progress value={fetchProgress} className="flex-1" />
              <span className="text-sm font-semibold text-primary min-w-[50px] text-right">
                {fetchProgress}%
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* Status Bar */}
      {!loading && refreshStatus !== 'idle' && (
        <div className={cn(
          "fixed top-0 left-0 right-0 h-1 z-50 transition-all duration-300",
          refreshStatus === 'success' && "bg-green-500",
          refreshStatus === 'mixed' && "bg-orange-500",
          refreshStatus === 'error' && "bg-red-500"
        )}>
          <div className="h-full transition-all duration-500" style={{ width: '100%' }} />
        </div>
      )}
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-foreground flex items-center gap-3 mb-2">
              <TrendingUp className="h-10 w-10 text-primary" />
              GainTracker
            </h1>
            <div className="flex items-center gap-2 mb-2 ml-1">
              <img 
                src={tradeCompassLogo} 
                alt="TradeCompass" 
                className="h-8 w-auto"
              />
              <span className="text-xs text-muted-foreground font-medium">
                Provided by TradeCompass
              </span>
            </div>
            <p className="text-muted-foreground">
              Portfolio Parsing
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              size="lg"
              onClick={handleRefreshPrices}
              disabled={loading || positions.length === 0}
            >
              <RefreshCw className={`mr-2 h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              Refresh Analysis
            </Button>
            <ExcelImport onImportComplete={loadPositions} />
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/ibkr-import')}
            >
              <FileBarChart className="mr-2 h-5 w-5" />
              IBKR Import
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/revolut-import')}
            >
              <FileBarChart className="mr-2 h-5 w-5" />
              Revolut Import
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/db-order')}
            >
              <ClipboardList className="mr-2 h-5 w-5" />
              DB Order
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/sma-surveillance')}
            >
              <Search className="mr-2 h-5 w-5" />
              SMA Surveillance
            </Button>
            <AddPositionDialog onAddPosition={handleAddPosition} />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="lg" disabled={positions.length === 0}>
                  <Trash2 className="mr-2 h-5 w-5" />
                  Reset Data
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete all {positions.length} of your imported position{positions.length !== 1 ? 's' : ''} from your account. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleResetData} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Delete My Data
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button variant="ghost" size="lg" onClick={handleSignOut}>
              <LogOut className="mr-2 h-5 w-5" />
              Sign Out
            </Button>
          </div>
        </div>
        
        {/* Portfolio Stats */}
        {analyses.length > 0 && (
          <>
            <div className="mb-8">
              <PortfolioStats {...stats} alertCounts={alertCounts} />
            </div>
            
            {/* Stock Table - All Positions */}
            <div className="mb-8">
              <StockTable 
                analyses={sortedAnalyses}
                onRowClick={scrollToCard}
              />
            </div>
            
            {/* Stock Scorecards - Action Recommendations */}
            <div className="mb-8">
              <StockScorecard analyses={sortedAnalyses} />
            </div>
            
            {/* Fibonacci Retracement Calculator */}
            <div className="mb-8">
              <FibonacciRetracement />
            </div>
            
            {/* Sort Controls for Position Cards */}
            <div className="flex items-center gap-3 mb-6 mt-8">
              <ArrowUpDown className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Sort cards by:</span>
              <Select value={sortBy} onValueChange={(value) => setSortBy(value as SortOption)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="totalValue">Total Value</SelectItem>
                  <SelectItem value="gainLoss">Gain/Loss</SelectItem>
                  <SelectItem value="addedValue">Added Value (CHF)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        
        {/* Positions Grid */}
        {analyses.length === 0 && positions.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-block p-6 bg-muted rounded-full mb-4">
              <TrendingUp className="h-12 w-12 text-muted-foreground" />
            </div>
            <h2 className="text-2xl font-semibold text-foreground mb-2">
              No Positions Yet
            </h2>
            <p className="text-muted-foreground mb-6">
              Add your first stock position to start tracking performance
            </p>
            <AddPositionDialog onAddPosition={handleAddPosition} />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {sortedAnalyses.map(analysis => (
                <div 
                  key={analysis.id}
                  ref={(el) => (cardRefs.current[analysis.id] = el)}
                  className="transition-all duration-300"
                >
                  <PositionCard
                    analysis={analysis}
                    onDelete={handleDeletePosition}
                  />
                </div>
              ))}
            </div>
            
            {/* Added Value Section - Clear Visual Separation */}
            <div className="mt-40 pt-12 border-t-4 border-primary/20 relative z-0">
              <div className="mb-10">
                <h2 className="text-4xl font-bold text-foreground flex items-center gap-3 mb-3">
                  <TrendingUp className="h-10 w-10 text-primary" />
                  Portfolio Added Value Analysis
                </h2>
                <p className="text-lg text-muted-foreground">
                  Detailed breakdown of stocks contributing positive value to your portfolio
                </p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                {/* Summary Statistics Cards */}
                <Card className="shadow-lg">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-2xl">Portfolio Summary</CardTitle>
                    <CardDescription>Key metrics from top performers</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {(() => {
                        const positiveStocks = sortedAnalyses.filter(a => (a.addedValueChf || 0) > 0);
                        const totalAddedValue = positiveStocks.reduce((sum, a) => sum + (a.addedValueChf || 0), 0);
                        const avgGain = positiveStocks.length > 0 
                          ? (positiveStocks.reduce((sum, a) => sum + ((a.gainPct || 0) * 100), 0) / positiveStocks.length) 
                          : 0;
                        
                        return (
                          <>
                            <div className="flex justify-between items-center p-4 bg-primary/5 rounded-lg">
                              <span className="font-medium text-foreground">Total Profitable Stocks</span>
                              <span className="text-2xl font-bold text-primary">{positiveStocks.length}</span>
                            </div>
                            <div className="flex justify-between items-center p-4 bg-green-500/10 rounded-lg">
                              <span className="font-medium text-foreground">Total Added Value</span>
                              <span className="text-2xl font-bold text-green-600">CHF {totalAddedValue.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center p-4 bg-blue-500/10 rounded-lg">
                              <span className="font-medium text-foreground">Average Gain</span>
                              <span className="text-2xl font-bold text-blue-600">+{avgGain.toFixed(2)}%</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Pie Chart */}
                <div className="h-full">
                  <PortfolioGainsPieChart analyses={analyses} />
                </div>
              </div>
              
              {/* Contributors Table */}
              <Card className="shadow-lg">
                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl flex items-center gap-2">
                    <TrendingUp className="h-6 w-6 text-primary" />
                    Contributors
                  </CardTitle>
                  <CardDescription>All stocks with positive added value, color-coded by performance</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-border">
                          <th className="text-left py-4 px-4 font-bold text-foreground">Rank</th>
                          <th className="text-left py-4 px-4 font-bold text-foreground">Stock</th>
                          <th className="text-right py-4 px-4 font-bold text-foreground">Added Value (CHF)</th>
                          <th className="text-right py-4 px-4 font-bold text-foreground">Gain %</th>
                          <th className="text-right py-4 px-4 font-bold text-foreground">Total Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const contributors = sortedAnalyses
                            .filter(a => (a.addedValueChf || 0) > 0)
                            .sort((a, b) => (b.addedValueChf || 0) - (a.addedValueChf || 0));
                          
                          const total = contributors.length;
                          const topThird = Math.ceil(total / 3);
                          const middleThird = Math.ceil((total * 2) / 3);
                          
                          return contributors.map((analysis, index) => {
                            const currencySymbol = analysis.localCurrency === 'USD' ? '$' : 
                                                   analysis.localCurrency === 'EUR' ? '€' : 'CHF';
                            
                            // Color coding based on rank
                            let rowClass = 'bg-yellow-50/30 dark:bg-yellow-900/10'; // Lowest (yellow)
                            let rankBgClass = 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400';
                            
                            if (index < topThird) {
                              // Best performers (green)
                              rowClass = 'bg-green-50/50 dark:bg-green-900/20';
                              rankBgClass = 'bg-green-500/30 text-green-800 dark:text-green-400';
                            } else if (index < middleThird) {
                              // Middle performers (blue)
                              rowClass = 'bg-blue-50/30 dark:bg-blue-900/10';
                              rankBgClass = 'bg-blue-500/20 text-blue-700 dark:text-blue-400';
                            }
                            
                            return (
                              <tr key={analysis.id} className={`border-b border-border/30 hover:brightness-95 transition-all ${rowClass}`}>
                                <td className="py-4 px-4">
                                  <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold ${rankBgClass}`}>
                                    {index + 1}
                                  </div>
                                </td>
                                <td className="py-4 px-4">
                                  <div className="font-semibold text-foreground">{analysis.ticker}</div>
                                </td>
                                <td className="py-4 px-4 text-right">
                                  <span className="text-lg font-bold text-green-700 dark:text-green-400">
                                    CHF {(analysis.addedValueChf || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </td>
                                <td className="py-4 px-4 text-right">
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 animate-blink">
                                    +{((analysis.gainPct || 0) * 100).toFixed(2)}%
                                  </span>
                                </td>
                                <td className="py-4 px-4 text-right">
                                  <span className="font-medium text-muted-foreground">
                                    {currencySymbol} {(analysis.totalValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-6 flex items-center gap-6 justify-center text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-green-500/30"></div>
                      <span className="text-muted-foreground">Best Performers</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-blue-500/20"></div>
                      <span className="text-muted-foreground">Middle Performers</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-yellow-500/20"></div>
                      <span className="text-muted-foreground">Lower Contributors</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Index;
